"use client";

import {
  SearchSelect,
  SearchSelectContent,
  SearchSelectItem,
  SearchSelectTrigger,
  SearchSelectValue,
} from "@/components/ui/select";

import { useProviders } from "@/hooks/use-providers";

import { formatStaffName } from "@/lib/format-data";

import type { FirebaseProviderSummary } from "@/types/provider";

export interface SingleProviderSelectProps {
  value?: string;
  onValueChange: (providerId: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowClear?: boolean;
  triggerId?: string;
  /** Outlines the trigger in red. The message is left to the caller to place. */
  hasError?: boolean;
  /** The providers to offer. Defaults to every active provider. */
  providers?: FirebaseProviderSummary[];
  /** Loading state for a caller-supplied `providers` list. */
  isLoading?: boolean;
}

export function SingleProviderSelect({
  value,
  onValueChange,
  placeholder = "Select a provider...",
  disabled = false,
  className,
  allowClear = true,
  triggerId,
  hasError,
  providers: providersOverride,
  isLoading: isLoadingOverride,
}: SingleProviderSelectProps) {
  // Fetch all providers (SearchSelectContent will handle filtering internally)
  const allProviders = useProviders({ showArchived: false });
  const providers = providersOverride ?? allProviders.providers;
  const isLoading = providersOverride
    ? Boolean(isLoadingOverride)
    : allProviders.isLoading;

  return (
    <div className={className}>
      <SearchSelect
        value={value || ""}
        onValueChange={(newValue) => {
          if (newValue === "" && allowClear) {
            onValueChange(undefined);
          } else if (newValue !== "") {
            onValueChange(newValue);
          }
        }}
      >
        <SearchSelectTrigger
          id={triggerId}
          disabled={disabled}
          hasError={hasError}
        >
          <SearchSelectValue
            placeholder={placeholder}
            formatValue={(val) => {
              if (!val) return "";
              const provider = providers.find((p) => p.id === val);
              return provider ? formatStaffName(provider) : val;
            }}
          />
        </SearchSelectTrigger>
        <SearchSelectContent
          searchPlaceholder="Search providers..."
          emptyMessage={
            isLoading ? "Loading providers..." : "No providers found"
          }
        >
          {allowClear && value && (
            <SearchSelectItem value="">
              <span className="text-muted-foreground">Clear selection</span>
            </SearchSelectItem>
          )}
          {providers.map((provider) => (
            <SearchSelectItem key={provider.id} value={provider.id}>
              {formatStaffName(provider)}
            </SearchSelectItem>
          ))}
        </SearchSelectContent>
      </SearchSelect>
    </div>
  );
}
